/**
 * What every group of the style bar's tools reads and writes: the selection's style values, and
 * the words' look (the selected words' while some are, else the whole item's).
 */
import { createMemo } from "solid-js";
import type { App } from "../../../../app/context";
import type { Bar } from "../../../../state/board";
import { WORDS_PX } from "../../../../shared/elements";
import type { RunKey } from "../../../../shared/richText";
import { hasRange, textEditor, type TextEditor } from "../textEditor";

export type { Bar } from "../../../../state/board";

/** Builds the bar's state from the app's services. */
export function createBar({ board, view, graph, kinds }: Pick<App, "board" | "view" | "graph" | "kinds">): Bar {
  const link = () => (view.state.selection?.type === "link" ? view.state.selection.link : null);
  const shape = () => {
    const id = board.selected()[0];
    const node = id ? graph.state.graph.nodes.find((n) => n.id === id) : undefined;
    return node ? kinds.board(node.kind).element : "frame";
  };
  const value = (key: string) => {
    const l = link();
    if (l) return board.styleOf(l, key);
    const id = board.selected()[0];
    return id ? board.styleOf(id, key) : "";
  };
  /** The words being written in this item, if any. */
  const editor = (): TextEditor | null => {
    const e = textEditor();
    return e && e.id === board.selected()[0] ? e : null;
  };
  /** The words being written, when some of them are selected: the bar then changes only those. */
  const ranged = () => {
    const e = editor();
    return hasRange(e) ? e : null;
  };
  const textValue = (key: RunKey) => {
    const e = ranged();
    return e ? (e.value(key) ?? "") : value(key);
  };
  const put = (key: RunKey, v: string) => {
    const e = ranged();
    if (e) return e.apply(key, v);
    const open = editor();
    // A highlight is only ever the words': all of them.
    if (open && key === "highlight") return open.apply(key, v, true);
    open?.clear(key);
    void board.setStyle(key, v);
  };
  return {
    link,
    shape,
    value,
    set: (key, v) => void board.setStyle(key, value(key) === v ? "" : v),
    colourKey: () => (link() ? "stroke" : shape() === "text" ? "color" : "fill"),
    textValue,
    put,
    toggle: (key, v) => put(key, textValue(key) === v ? "" : v),
    size: createMemo(() => Number(textValue("size")) || (kinds.element(shape()).wordsPx ?? WORDS_PX)),
    many: () => board.selected().length > 1,
  };
}

/** Whether a style value is a colour the colour fields can show (`#rrggbb`). */
export const isHexColour = (v: string) => /^#[0-9a-f]{6}$/i.test(v);
